<app-layout [enableTopGap]="true">
  <section class="quiz-page">
    <div class="quiz-shell">
      <div *ngIf="loading" class="quiz-state" role="status">
        <i class="fas fa-circle-notch fa-spin" aria-hidden="true"></i>
        <span>{{ "quiz.loading" | translate }}</span>
      </div>

      <div *ngIf="!loading && notFound" class="quiz-state">
        <i class="fas fa-search" aria-hidden="true"></i>
        <span>{{ "quiz.notFound" | translate }}</span>
        <a class="quiz-btn primary" [routerLink]="listLink">{{ "quiz.results.backToQuizList" | translate }}</a>
      </div>

      <ng-container *ngIf="!loading && quiz && currentQuestion as question">
        <p *ngIf="isFallback" class="quiz-notice" role="note">
          <i class="fas fa-language" aria-hidden="true"></i>
          {{ "quizes.languageFallback" | translate }}
        </p>

        <header class="quiz-toolbar">
          <a class="quiz-icon-btn" [routerLink]="listLink" [attr.aria-label]="'quiz.results.backToQuizList' | translate">
            <i class="fas fa-arrow-left" aria-hidden="true"></i>
          </a>
          <div class="quiz-title">
            <h1>{{ "quizes.ticket" | translate: { number: quiz.id } }}</h1>
            <span class="quiz-chip" [class.practice]="mode === 'practice'">
              {{ "quizes.mode." + mode | translate }}
            </span>
            <span *ngIf="v2Category as category" class="quiz-chip edition">2026 · {{ category }}</span>
          </div>
          <div *ngIf="mode === 'exam'" class="quiz-timer" [ngClass]="timerState" role="timer"
            [attr.aria-label]="'quiz.timeLeft' | translate">
            <i class="fas fa-clock" aria-hidden="true"></i>
            <span>{{ formattedTime }}</span>
          </div>
          <div *ngIf="mode === 'practice'" class="quiz-timer">
            <i class="fas fa-check" aria-hidden="true"></i>
            <span>{{ correctCount }} / {{ answeredCount }}</span>
          </div>
          <button type="button" class="quiz-btn ghost quiz-finish" (click)="requestFinish()">
            {{ "quiz.finish" | translate }}
          </button>
        </header>

        <div class="quiz-progress" role="progressbar" aria-valuemin="0" [attr.aria-valuemax]="questions.length"
          [attr.aria-valuenow]="answeredCount" [attr.aria-label]="'quiz.answered' | translate: { count: answeredCount, total: questions.length }">
          <span [style.width.%]="progressPercent"></span>
        </div>

        <nav class="quiz-navigator" [attr.aria-label]="'quiz.questions' | translate">
          <button *ngFor="let item of questions; let i = index; trackBy: trackByIndex" type="button" class="quiz-nav-item"
            [class.current]="i === currentIndex"
            [class.answered]="answers[i] !== null && mode === 'exam'"
            [class.correct]="mode === 'practice' && answers[i] !== null && isCorrect(i)"
            [class.wrong]="mode === 'practice' && answers[i] !== null && !isCorrect(i)"
            [attr.aria-current]="i === currentIndex ? 'step' : null"
            [attr.aria-label]="'quiz.questionOf' | translate: { current: i + 1, total: questions.length }"
            (click)="goTo(i)">
            {{ i + 1 }}
          </button>
        </nav>

        <article #questionCard class="quiz-card" [class.just-answered]="justAnswered" (touchstart)="onTouchStart($event)" (touchend)="onTouchEnd($event)">
          <p class="quiz-counter">
            {{ "quiz.questionOf" | translate: { current: currentIndex + 1, total: questions.length } }}
          </p>
          <h2 id="quiz-question-text" class="quiz-question" dir="auto">{{ question.question }}</h2>

          <app-quiz-image *ngIf="question.imagePath" [src]="question.imagePath"
            [alt]="'quiz.image_alt' | translate"></app-quiz-image>

          <div class="quiz-options" role="radiogroup" aria-labelledby="quiz-question-text">
            <button *ngFor="let option of optionViews[currentIndex]; let i = index; trackBy: trackByIndex" type="button" role="radio"
              class="quiz-option"
              [class.selected]="currentAnswer === i"
              [class.correct]="showFeedback && isCorrectOption(i)"
              [class.wrong]="showFeedback && currentAnswer === i && !isCorrectOption(i)"
              [class.locked]="showFeedback"
              [attr.aria-checked]="currentAnswer === i"
              [attr.aria-disabled]="showFeedback || null"
              (click)="selectAnswer(i); releasePointerFocus($event)">
              <span class="quiz-option-badge" aria-hidden="true">{{ option.label }}</span>
              <span class="quiz-option-text" dir="auto">{{ option.text }}</span>
              <i *ngIf="showFeedback && isCorrectOption(i)" class="fas fa-check quiz-option-icon" aria-hidden="true"></i>
              <i *ngIf="showFeedback && currentAnswer === i && !isCorrectOption(i)" class="fas fa-times quiz-option-icon"
                aria-hidden="true"></i>
            </button>
          </div>

          <p *ngIf="showFeedback" class="quiz-feedback" [class.correct]="isCorrect(currentIndex)" aria-live="polite">
            <ng-container *ngIf="isCorrect(currentIndex); else wrongFeedback">
              <i class="fas fa-check-circle" aria-hidden="true"></i> {{ "quiz.feedbackCorrect" | translate }}
            </ng-container>
            <ng-template #wrongFeedback>
              <i class="fas fa-times-circle" aria-hidden="true"></i>
              {{ "quiz.feedbackWrong" | translate: { answer: question.correctAnswerIndex } }}
            </ng-template>
          </p>
        </article>

        <p class="quiz-hint">{{ "quiz.keyboardHint" | translate }}</p>

        <footer class="quiz-actions">
          <button type="button" class="quiz-btn secondary" (click)="previous()" [disabled]="currentIndex === 0">
            <i class="fas fa-chevron-left" aria-hidden="true"></i>
            <span class="quiz-btn-label">{{ "quiz.previous" | translate }}</span>
          </button>
          <span class="quiz-actions-status">
            <span *ngIf="mode === 'exam'" class="quiz-actions-timer" [ngClass]="timerState">
              <i class="fas fa-clock" aria-hidden="true"></i> {{ formattedTime }}
            </span>
            <span class="quiz-actions-count">{{ "quiz.answered" | translate: { count: answeredCount, total: questions.length } }}</span>
          </span>
          <button type="button" class="quiz-btn primary" (click)="next()">
            <ng-container *ngIf="!isLastQuestion; else finishLabel">
              <span class="quiz-btn-label">{{ "quiz.next" | translate }}</span>
              <i class="fas fa-chevron-right" aria-hidden="true"></i>
            </ng-container>
            <ng-template #finishLabel>
              <span>{{ "quiz.finish" | translate }}</span>
              <i class="fas fa-flag-checkered" aria-hidden="true"></i>
            </ng-template>
          </button>
        </footer>
      </ng-container>
    </div>
  </section>
</app-layout>

<app-modal
  [show]="showFinishConfirm"
  tone="warning"
  [dismissible]="true"
  [title]="'quiz.confirmFinishTitle' | translate"
  [message]="'quiz.confirmFinishText' | translate: { count: unansweredCount }"
  [primaryLabel]="'quiz.finish' | translate"
  [secondaryLabel]="'quiz.reviewUnanswered' | translate"
  (close)="finish(false)"
  (secondary)="goToFirstUnanswered()"
  (dismiss)="showFinishConfirm = false"
></app-modal>

<app-modal
  [show]="!!result"
  [tone]="result?.passed ? 'success' : 'danger'"
  [title]="resultTitle"
  [message]="resultMessage"
  [secondaryLabel]="'quiz.tryAgain' | translate"
  (close)="showResults()"
  (secondary)="restart()"
></app-modal>
